/**
 * Centralized Navigation Configuration
 *
 * This file defines all navigation items with their role and module requirements.
 * Used by Navbar, QuickActions, and GlobalSearch components.
 */

// Role groups for easier permission management
export const ROLE_GROUPS = {
  // Superadmin: Platform owner only - full unrestricted access
  SUPERADMIN: ["superadmin"],
  // Admin: University's highest role - full admin within enabled modules
  ADMIN: ["admin", "superadmin"],
  FINANCE: ["admin", "superadmin", "finance", "controller", "accountant"],
  TUITION: ["admin", "superadmin", "tuition_planner", "tuition_approver"],
  ADMISSIONS: ["admin", "superadmin", "tuition_planner", "admissions"],
  COURSES: ["admin", "superadmin", "course_planner", "course_approver"],
  FORMS: ["admin", "superadmin", "forms", "form_admin"],
  HR: ["admin", "superadmin", "hr", "dept_manager"],
  BUDGET: ["admin", "superadmin", "budget_owner", "dept_manager", "finance"],
};

/**
 * Check if user is a superadmin
 * @param {Object} user - User object with is_superadmin flag or role_slugs
 * @returns {boolean}
 */
export function isSuperAdmin(user) {
  if (!user) return false;
  // Check is_superadmin flag first (from backend)
  if (user.is_superadmin === true) return true;
  // Fallback to role check
  const roles = user.role_slugs || [];
  return roles.some((r) => String(r).toLowerCase() === "superadmin");
}

/**
 * Check if user is an admin (includes superadmin)
 * @param {Object} user - User object with is_admin flag or role_slugs
 * @returns {boolean}
 */
export function isAdmin(user) {
  if (!user) return false;
  // Check flags first (from backend)
  if (user.is_admin === true || user.is_superadmin === true) return true;
  // Fallback to role check
  const roles = user.role_slugs || [];
  return roles.some((r) =>
    ["admin", "superadmin"].includes(String(r).toLowerCase()),
  );
}

// Icon mappings for navigation items (using Lucide icon names)
export const NAV_ICONS = {
  home: "LayoutDashboard",
  campus_summary: "Building2",
  transfers: "ArrowLeftRight",
  position_budgeting: "Users",
  planning: "Calendar",
  research: "FlaskConical",
  tuition_planning: "GraduationCap",
  budget_model: "Calculator",
  budget_vs_actual: "BarChart3",
  budget_auxiliary: "Landmark",
  budget_scenarios: "GitBranch",
  program_margins: "PieChart",
  revenue_recognition: "DollarSign",
  enrollment_funnel: "Filter",
  yield_analysis: "TrendingUp",
  reports: "FileText",
  courses: "BookOpen",
  admin: "Settings",
  forms: "ClipboardList",
};

// Categories for grouping navigation items
export const NAV_CATEGORIES = {
  CORE: "core",
  BUDGET: "budget",
  TUITION: "tuition",
  POSITIONS: "positions",
  RESEARCH: "research",
  CAPITAL: "capital",
  REPORTS: "reports",
  ADMIN: "admin",
};

/**
 * Base navigation items configuration
 *
 * Each item can have:
 * - key: unique identifier
 * - label: display text
 * - path: route path
 * - icon: icon name from NAV_ICONS
 * - category: grouping category
 * - requiresAuth: whether login is required
 * - priority: 'primary' (always visible) or 'secondary' (in More dropdown)
 * - roles: array of role slugs that can see this item (empty = all roles)
 * - modules: array of module keys required (empty = no module required)
 * - description: short description for search/quick actions
 * - keywords: additional search terms
 */
export const BASE_NAV_ITEMS = [
  // Core navigation
  {
    key: "home",
    label: "Dashboard",
    path: "/",
    icon: "home",
    category: NAV_CATEGORIES.CORE,
    requiresAuth: true,
    priority: "primary",
    roles: [],
    modules: [],
    description: "Your personalized dashboard with tasks and notifications",
    keywords: ["tasks", "notifications", "overview", "my"],
  },
  {
    key: "campus_summary",
    label: "Campus Summary",
    path: "/campus-summary",
    icon: "campus_summary",
    category: NAV_CATEGORIES.CORE,
    requiresAuth: true,
    priority: "primary",
    roles: [],
    modules: [],
    description: "Campus-wide financial summary",
    keywords: ["overview", "institution", "total"],
  },
  {
    key: "transfers",
    label: "Transfers",
    path: "/transfers",
    icon: "transfers",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: [],
    description: "Budget transfers between departments",
    keywords: ["move", "funds", "reallocate"],
  },

  // Position Management
  {
    key: "position_budgeting",
    label: "Position Budgeting",
    path: "/positions/budgeting",
    icon: "position_budgeting",
    category: NAV_CATEGORIES.POSITIONS,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["positions"],
    description: "Manage position budgets and allocations",
    keywords: ["personnel", "staff", "salary", "compensation"],
  },
  {
    key: "approvals",
    label: "Approvals",
    path: "/approvals",
    icon: "position_budgeting",
    category: NAV_CATEGORIES.POSITIONS,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["positions"],
    description: "Review and approve allocation changes",
    keywords: ["pending", "review", "workflow", "approve", "reject"],
  },
  {
    key: "my-submissions",
    label: "My Submissions",
    path: "/my-submissions",
    icon: "position_budgeting",
    category: NAV_CATEGORIES.POSITIONS,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["positions"],
    description: "Track your allocation change requests",
    keywords: ["my", "submissions", "requests", "track", "status"],
  },

  // Planning
  {
    key: "planning",
    label: "Planning",
    path: "/planning",
    icon: "planning",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: [],
    description: "Budget planning and forecasting",
    keywords: ["forecast", "projection", "annual"],
  },

  // Budget Model (Unified Budgeting)
  {
    key: "budget_model",
    label: "Budget Model",
    path: "/budget-model",
    icon: "budget_model",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "primary",
    roles: ["admin", "superadmin", "budget_owner"],
    modules: [],
    description: "Unified financial planning with live projections",
    keywords: [
      "scenario",
      "projection",
      "assumptions",
      "unified",
      "model",
      "forecast",
    ],
  },

  // Research
  {
    key: "research",
    label: "Research",
    path: "/research",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Research grants and commitments",
    keywords: ["grants", "sponsored", "projects"],
  },
  {
    key: "research_proposals",
    label: "Proposals",
    path: "/research/proposals",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Manage research proposals and submissions",
    keywords: ["proposals", "grants", "submissions", "applications"],
  },
  {
    key: "research_limited_submissions",
    label: "Limited Submissions",
    path: "/research/limited-submissions",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Internal competitions for limited submission opportunities",
    keywords: ["limited", "competition", "internal", "selection"],
  },
  {
    key: "research_jit_requests",
    label: "JIT Requests",
    path: "/research/jit-requests",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Track Just-In-Time information requests from sponsors",
    keywords: ["jit", "just-in-time", "sponsor", "requests", "information"],
  },
  {
    key: "research_pas_requests",
    label: "Pre-Award Spending",
    path: "/research/pas-requests",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Manage pre-award spending requests and at-risk spending",
    keywords: ["pas", "pre-award", "spending", "at-risk"],
  },
  {
    key: "research_document_rules",
    label: "Document Rules",
    path: "/research/document-rules",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: ["admin", "superadmin", "grant_admin", "research_director"],
    modules: ["research"],
    description: "Configure sponsor document validation rules",
    keywords: ["document", "validation", "rules", "sponsor", "formatting"],
  },
  {
    key: "research_approval_workflows",
    label: "Approval Workflows",
    path: "/research/approval-workflows",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: ["admin", "superadmin", "research_director"],
    modules: ["research"],
    description: "Configure proposal approval routing and steps",
    keywords: ["approval", "workflow", "routing", "hierarchy", "sign-off"],
  },
  {
    key: "research_training_compliance",
    label: "Training Compliance",
    path: "/research/training-compliance",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Track required training certifications and expirations",
    keywords: ["training", "compliance", "citi", "certification", "expiration"],
  },
  {
    key: "research_fa_waiver_requests",
    label: "F&A Waiver Requests",
    path: "/research/fa-waiver-requests",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["research"],
    description: "Request reduced indirect cost rates for proposals",
    keywords: ["fa", "waiver", "indirect", "cost", "rate", "reduction"],
  },
  {
    key: "research_submission_settings",
    label: "Submission Settings",
    path: "/research/submission-settings",
    icon: "research",
    category: NAV_CATEGORIES.RESEARCH,
    requiresAuth: true,
    priority: "secondary",
    roles: ["admin", "superadmin", "research_director"],
    modules: ["research"],
    description: "Configure proposal submission workflow and deadlines",
    keywords: ["submission", "deadline", "internal", "sla", "settings"],
  },

  // Capital Planning
  {
    key: "capital",
    label: "Capital Projects",
    path: "/capital",
    icon: "campus_summary",
    category: NAV_CATEGORIES.CAPITAL,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["capital"],
    description: "Capital projects and multi-year investments",
    keywords: [
      "construction",
      "assets",
      "buildings",
      "equipment",
      "infrastructure",
    ],
  },
  {
    key: "capital_planning",
    label: "Capital Planning",
    path: "/capital/planning",
    icon: "planning",
    category: NAV_CATEGORIES.CAPITAL,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["capital"],
    description: "Capital expenditure projections and analysis",
    keywords: ["projections", "depreciation", "debt service", "balance sheet"],
  },

  // Tuition & Revenue
  {
    key: "tuition_planning",
    label: "Tuition Planning",
    path: "/fees/projection",
    icon: "tuition_planning",
    category: NAV_CATEGORIES.TUITION,
    requiresAuth: true,
    priority: "secondary",
    roles: ROLE_GROUPS.TUITION,
    modules: ["tuition"],
    description: "Plan and project tuition rates",
    keywords: ["fees", "rates", "pricing"],
  },
  {
    key: "enrollment_funnel",
    label: "Enrollment Funnel",
    path: "/enrollment/funnel",
    icon: "enrollment_funnel",
    category: NAV_CATEGORIES.TUITION,
    requiresAuth: true,
    priority: "secondary",
    roles: ROLE_GROUPS.ADMISSIONS,
    modules: ["tuition"],
    description: "Track enrollment pipeline stages",
    keywords: ["applicants", "admitted", "enrolled", "pipeline"],
  },
  {
    key: "yield_analysis",
    label: "Yield Analysis",
    path: "/enrollment/yield",
    icon: "yield_analysis",
    category: NAV_CATEGORIES.TUITION,
    requiresAuth: true,
    priority: "secondary",
    roles: ROLE_GROUPS.ADMISSIONS,
    modules: ["tuition"],
    description: "Analyze enrollment yield rates",
    keywords: ["conversion", "acceptance", "melt"],
  },

  // Budget & Finance
  {
    key: "budget_vs_actual",
    label: "Budget vs Actual",
    path: "/budget/vs-actual",
    icon: "budget_vs_actual",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: [],
    description: "Compare budgeted vs actual spending",
    keywords: ["variance", "comparison", "spending"],
  },
  {
    key: "budget_book",
    label: "Budget Book",
    path: "/budget/book",
    icon: "budget_auxiliary",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: [],
    description: "Comprehensive budget report by department",
    keywords: [
      "budget",
      "report",
      "division",
      "department",
      "summary",
      "book",
      "pdf",
    ],
  },
  {
    key: "budget_scenarios",
    label: "Budget Scenarios",
    path: "/budget/scenarios",
    icon: "budget_scenarios",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: [],
    description: "Create and compare budget scenarios",
    keywords: ["what-if", "alternatives", "models"],
  },
  {
    key: "program_margins",
    label: "Program Margins",
    path: "/budget/margins",
    icon: "program_margins",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: [],
    description: "Analyze program profitability",
    keywords: ["profit", "cost", "revenue", "analysis"],
  },
  {
    key: "revenue_recognition",
    label: "Revenue Recognition",
    path: "/budget/revenue-recognition",
    icon: "revenue_recognition",
    category: NAV_CATEGORIES.BUDGET,
    requiresAuth: true,
    priority: "secondary",
    roles: ROLE_GROUPS.FINANCE,
    modules: [],
    description: "Manage revenue recognition schedules",
    keywords: ["deferred", "accrual", "accounting"],
  },

  // Reporting
  {
    key: "reports",
    label: "Analytics BI",
    path: "/superset",
    icon: "reports",
    category: NAV_CATEGORIES.REPORTS,
    requiresAuth: true,
    priority: "secondary",
    roles: [],
    modules: ["reporting"],
    description: "Access analytics dashboards and reports",
    keywords: ["analytics", "dashboards", "visualizations"],
  },

  // Courses
  {
    key: "courses",
    label: "Courses",
    path: "/courses",
    icon: "courses",
    category: NAV_CATEGORIES.REPORTS,
    requiresAuth: true,
    priority: "secondary",
    roles: ROLE_GROUPS.COURSES,
    modules: ["courses"],
    description: "Course planning and scheduling",
    keywords: ["classes", "sections", "schedule"],
  },
];

/**
 * Quick actions configuration
 * These are contextual shortcuts shown based on user role and current page
 */
export const QUICK_ACTIONS = [
  // Help & AI Assistant - available to all users
  {
    key: "help_assistant",
    label: "Help & AI Assistant",
    path: "/help",
    icon: "Sparkles",
    roles: [],
    description: "Get help and ask AI for guidance",
  },

  // Admin actions
  {
    key: "manage_users",
    label: "Manage Users",
    path: "/admin#/users",
    icon: "Users",
    roles: ROLE_GROUPS.ADMIN,
    description: "Add or edit user accounts",
  },
  {
    key: "manage_roles",
    label: "Manage Roles",
    path: "/admin#/roles",
    icon: "Shield",
    roles: ROLE_GROUPS.ADMIN,
    description: "Configure roles and permissions",
  },
  {
    key: "role_permissions",
    label: "Role Permissions",
    path: "/admin/permissions",
    icon: "ShieldCheck",
    roles: ROLE_GROUPS.ADMIN,
    description: "Manage role permission matrix",
  },
  {
    key: "system_settings",
    label: "UI Settings",
    path: "/admin#/ui-settings",
    icon: "Settings",
    roles: ROLE_GROUPS.ADMIN,
    description: "Configure UI settings",
  },

  // Superadmin-only actions
  {
    key: "license_settings",
    label: "License & Modules",
    path: "/admin#/license-settings",
    icon: "Key",
    roles: ROLE_GROUPS.SUPERADMIN,
    description: "Manage license and enable/disable modules",
    superadminOnly: true,
  },
  {
    key: "sql_runner",
    label: "SQL Runner",
    path: "/admin#/sql-runner",
    icon: "Terminal",
    roles: ROLE_GROUPS.SUPERADMIN,
    description: "Run SQL queries directly",
    superadminOnly: true,
  },
  {
    key: "data_seeder",
    label: "Data Seeder",
    path: "/admin#/data-seeder",
    icon: "Database",
    roles: ROLE_GROUPS.SUPERADMIN,
    description: "Seed demo data",
    superadminOnly: true,
  },

  // Budget actions
  {
    key: "new_transfer",
    label: "New Transfer",
    path: "/transfers?new=true",
    icon: "ArrowLeftRight",
    roles: ROLE_GROUPS.BUDGET,
    description: "Create a budget transfer",
  },
  {
    key: "view_budget",
    label: "View My Budget",
    path: "/budget",
    icon: "Wallet",
    roles: [],
    description: "View your department budget",
  },

  // HR/Position actions
  {
    key: "add_position",
    label: "Add Position",
    path: "/positions/budgeting?new=true",
    icon: "UserPlus",
    roles: ROLE_GROUPS.HR,
    modules: ["positions"],
    description: "Create a new position",
  },

  // Research actions
  {
    key: "new_commitment",
    label: "New Commitment",
    path: "/research?new=true",
    icon: "FileSignature",
    roles: [],
    modules: ["research"],
    description: "Create a research commitment",
  },
  {
    key: "new_proposal",
    label: "New Proposal",
    path: "/research/proposals?new=true",
    icon: "FilePlus",
    roles: [],
    modules: ["research"],
    description: "Create a new research proposal",
  },
  {
    key: "view_jit_requests",
    label: "JIT Requests",
    path: "/research/jit-requests",
    icon: "Clock",
    roles: [],
    modules: ["research"],
    description: "View Just-In-Time information requests",
  },
  {
    key: "view_pas_requests",
    label: "Pre-Award Spending",
    path: "/research/pas-requests",
    icon: "Banknote",
    roles: [],
    modules: ["research"],
    description: "View pre-award spending requests",
  },

  // Capital actions
  {
    key: "new_capital_project",
    label: "New Capital Project",
    path: "/capital?new=true",
    icon: "Building2",
    roles: [],
    modules: ["capital"],
    description: "Create a capital project",
  },
  {
    key: "capital_projections",
    label: "Capital Projections",
    path: "/capital/planning",
    icon: "TrendingUp",
    roles: [],
    modules: ["capital"],
    description: "View capital expenditure projections",
  },
];

/**
 * Check if user has any of the required roles
 * @param {string[]} userRoles - User's role slugs (lowercase)
 * @param {string[]} requiredRoles - Required role slugs
 * @returns {boolean}
 */
export function hasRole(userRoles, requiredRoles) {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  const normalizedUserRoles = userRoles.map((r) => String(r).toLowerCase());
  return requiredRoles.some((role) =>
    normalizedUserRoles.includes(role.toLowerCase()),
  );
}

/**
 * Check if required modules are enabled
 * @param {Function} isModuleEnabled - Module check function from LicenseContext
 * @param {string[]} requiredModules - Required module keys
 * @param {boolean} isAdmin - Whether user is admin (admins bypass module checks)
 * @returns {boolean}
 */
export function hasModule(isModuleEnabled, requiredModules, isAdmin = false) {
  if (!requiredModules || requiredModules.length === 0) return true;
  if (isAdmin) return true;
  return requiredModules.every((mod) => isModuleEnabled(mod));
}

/**
 * Check if user has any of the required permissions
 * @param {string[]} userPermissions - User's permission slugs
 * @param {string[]} requiredPermissions - Required permission slugs
 * @returns {boolean}
 */
export function hasPermission(userPermissions, requiredPermissions) {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  const normalizedUserPerms = userPermissions.map((p) =>
    String(p).toLowerCase(),
  );
  return requiredPermissions.some((perm) =>
    normalizedUserPerms.includes(perm.toLowerCase()),
  );
}

/**
 * Check if user can access a resource/action based on roles OR permissions
 * @param {Object} user - User object with role_slugs and permissions
 * @param {Object} requirements - { roles?: string[], permissions?: string[] }
 * @returns {boolean}
 */
export function canAccess(user, { roles = [], permissions = [] } = {}) {
  if (!user) return false;
  const userRoles = user.role_slugs || [];
  const userPermissions = user.permissions || [];

  // If no requirements, allow access
  if (roles.length === 0 && permissions.length === 0) return true;

  // Check roles first (legacy support)
  if (roles.length > 0 && hasRole(userRoles, roles)) return true;

  // Check permissions
  if (permissions.length > 0 && hasPermission(userPermissions, permissions))
    return true;

  return false;
}

/**
 * Filter navigation items based on user permissions
 * @param {Object} options
 * @param {string[]} options.userRoles - User's role slugs
 * @param {Function} options.isModuleEnabled - Module check function
 * @param {boolean} options.isAuthenticated - Whether user is logged in
 * @returns {Object[]} Filtered navigation items
 */
export function getVisibleNavItems({
  userRoles = [],
  isModuleEnabled = () => false,
  isAuthenticated = false,
}) {
  const normalizedRoles = userRoles.map((r) => String(r).toLowerCase());
  const isAdmin = hasRole(normalizedRoles, ROLE_GROUPS.ADMIN);

  return BASE_NAV_ITEMS.filter((item) => {
    // Check authentication
    if (item.requiresAuth && !isAuthenticated) return false;

    // Check roles
    if (!hasRole(normalizedRoles, item.roles)) return false;

    // Check modules
    if (!hasModule(isModuleEnabled, item.modules, isAdmin)) return false;

    return true;
  });
}

/**
 * Get quick actions for user based on their role
 * @param {Object} options
 * @param {string[]} options.userRoles - User's role slugs
 * @param {Function} options.isModuleEnabled - Module check function
 * @returns {Object[]} Available quick actions
 */
export function getQuickActions({
  userRoles = [],
  isModuleEnabled = () => false,
}) {
  const normalizedRoles = userRoles.map((r) => String(r).toLowerCase());
  const isAdmin = hasRole(normalizedRoles, ROLE_GROUPS.ADMIN);

  return QUICK_ACTIONS.filter((action) => {
    if (!hasRole(normalizedRoles, action.roles)) return false;
    if (action.modules && !hasModule(isModuleEnabled, action.modules, isAdmin))
      return false;
    return true;
  });
}

const navigationExports = {
  ROLE_GROUPS,
  NAV_ICONS,
  NAV_CATEGORIES,
  BASE_NAV_ITEMS,
  QUICK_ACTIONS,
  hasRole,
  hasModule,
  hasPermission,
  canAccess,
  getVisibleNavItems,
  getQuickActions,
};

export default navigationExports;
